// ---- NPC registry + manager ----
// Lists every NPC and drives them each frame: builds the mesh + nameplate +
// collider on init, optionally roams them between random waypoints, and shows a
// speech bubble over the head of any NPC the player is standing near. The bubble
// is a DOM element projected from the NPC's head world position to screen space,
// so it stays crisp at any zoom.
//
// Add an NPC: write a file with defineNpc (see king.ts) and add it to NPCS.

import * as THREE from "three";
import { buildNpc, type NpcSpec } from "./define.ts";
import { dressNpc } from "./dress.ts";
import { disposeGroup } from "../meshkit.ts";
import { makeNameLabel, NAMEPLATE_RANGE } from "../label.ts";
import { surfaceAt, navWorld, type Collider, type CircleCollider } from "../physics.ts";
import { occludedByCutaway, hideOutdoorWhileIndoor } from "../cutaway.ts";
import { NPCS, NPCS_BY_MAP, isDynamic } from "./roster.ts";
import { ACTIVE_MAP_ID } from "../map/active.ts";
import { modelDoc } from "../model/index.ts";
import { animateGait, gaitSnapshot, GAIT_WALK_SPEED, GAIT_RUN_SPEED, NPC_PROFILE, type GaitArchetype } from "../model/gait.ts";
import { collectRig, type RigHandle } from "../model/rig.ts";
import { applyWeaponPose, startRigAttack } from "../anim/pose.ts";
import { applyBodyPose } from "../anim/body-pose.ts";
import { CAM_FAR } from "../config.ts";
import type { BarCss } from "../playerbars.ts";
import { makeCbar, makeStatusIcons } from "../playerbars.ts";

/** Beyond this camera distance an entity is provably off-screen (the far plane
 *  clips at CAM_FAR and fog is opaque well before it) — gait/pose animation is
 *  skipped there. Shared by updateNpcs and net.ts's remote-player pass. */
export const ANIM_RADIUS = CAM_FAR + 10;

export { NPCS };

export type Live = {
  spec: NpcSpec;
  group: THREE.Group;
  collider: CircleCollider | null; // kept under the NPC's networked position so the player still bumps it
  bubble: HTMLDivElement;
  label: THREE.Sprite | null; // floating name tag (hidden while talking to it)
  height: number;
  feetY: number;        // resolved standing height (group.y minus walk bob)
  // dialogue
  line: number;
  inRange: boolean;
  bubbleActive: boolean;  // the proximity bubble greeted on THIS approach (gates display)
  bubbleCd: number;       // seconds until it can greet again (see BUBBLE_COOLDOWN)
  // combat — meaningful only when spec.combat is set. For dynamic NPCs these are
  // pushed in from the server each snapshot by net.ts (the server is authoritative
  // for combat); combat.ts reads them to drive the HUD + reaction animations.
  fightable: boolean;     // spec.combat != null
  hp: number; maxHp: number;
  stamina: number; maxStamina: number;
  engaged: boolean;       // in an active fight with a player right now (server-set)
  defeated: boolean;      // dropped to 0 hp — hidden + non-solid for the session
  defeatT: number;        // defeat-collapse animation progress (seconds)
  cbarWrap: HTMLDivElement | null; // overhead HP/STA bar container (projected)
  hpFill: HTMLDivElement | null;   // its HP fill
  staFill: HTMLDivElement | null;  // its stamina fill
  hpLabel: HTMLSpanElement | null; // numeric "cur/max" inside the HP bar
  staLabel: HTMLSpanElement | null;
  statusIcons: HTMLDivElement | null; // snare/stun icons under the bars
  snare: boolean;                  // server CC flags (wire present-only)
  stun: boolean;
  slow: boolean;
  cbarCss?: BarCss;                // last-written bar styles (combat.ts diff-gates via the shared playerbars.ts helpers)
  // talk — meaningful only when spec.talk is set (mirror of the combat fields).
  talkable: boolean;      // spec.talk != null
  talker: string | null;  // player id mid-conversation with it (server-set via net.ts)
  talkingToMe: boolean;   // = talker === me.id (derived in net.ts) — hides its bubble + name tag during our cutscene
  // plan 14 auto-engage: the player id this NPC is currently targeting (server-
  // set via net.ts). `tgt === me.id` = it started a fight with US — combat.ts
  // pops the engaged presentation with no click.
  tgt: string | null;
  // flee — wildlife only (server-set via net.ts). Drives a frantic scamper pose.
  fleeing: boolean;
  scamT: number;          // scamper hop phase accumulator
  // airborne (a ballistic fall — gravity plan, server-set via net.ts). Drives
  // the airborne pose; false for stationary/static NPCs.
  air: boolean;
  // gait — archetype-bodied NPCs (quadruped/humanoid/bird/bug) get the shared
  // limb animator (src/model/gait.ts). null for bespoke ("parts") / coded models.
  // Motion is inferred from the networked transform's frame-to-frame speed
  // (lastX/lastZ); the attack is triggered off net.ts's atkSeq edge.
  gaitArch: GaitArchetype | null;
  lastX: number; lastZ: number;
  // Weapon-pose rig — cached for humanoid-archetype NPCs so driveGait can run the
  // SAME per-weapon stance/swing pass as the player (pose.ts applyWeaponPose) when
  // one holds a weapon. null for non-humanoid / bespoke / coded models.
  rig: RigHandle | null;
  // interest management — true while this (dynamic) NPC is OUTSIDE our area of
  // interest: the server isn't streaming it, so it's invisible + non-solid here.
  // Distinct from `defeated` (a corpse is a visible gameplay state; netHidden is
  // pure network visibility). Dynamic NPCs start hidden and appear on their
  // first interest enter (net.ts); static NPCs never ride the wire and render
  // always. NOTE updateNpcs rewrites group.visible every frame (cutaway), so
  // this flag — not group.visible — is the source of truth.
  netHidden: boolean;
};

/** A focus-target handle is just a Live record — the focus module reads its
 *  group (live world position), feetY, name, and visibility from it. */
export type NpcHandle = Live;

const live: Live[] = [];
const byId = new Map<string, Live>(); // spec.id → Live, for net.ts to fold server state in

/** Every live NPC, exposed read-only so the focus system can raycast against
 *  them and track the focused one's (possibly moving) position. */
export function npcs(): readonly NpcHandle[] {
  return live;
}

/** Look up a live NPC by its stable spec id (used by net.ts to apply the
 *  server's authoritative state to the right record). */
export function liveById(id: string): Live | undefined {
  return byId.get(id);
}
const _v = new THREE.Vector3();

// Proximity chat-bubble cooldown: once an NPC greets the player on approach, it
// stays quiet for this long before it can greet again — so pacing back and forth
// past an NPC doesn't spam the same line. Per-NPC (Live.bubbleCd), ticks down
// every frame regardless of range.
const BUBBLE_COOLDOWN = 60; // seconds

/** The gait archetype for an NPC's model, or null for bespoke ("parts") / coded
 *  models that have no rigged limbs to drive. */
function gaitArchOf(spec: NpcSpec): GaitArchetype | null {
  const arch = spec.model ? modelDoc(spec.model)?.archetype : undefined;
  return arch && arch !== "parts" ? (arch as GaitArchetype) : null;
}

/** Drive one NPC's shared limb gait: infer walk/run from its networked speed and
 *  pose the legs/wings. Root transforms are left to net.ts (position) + combat.ts
 *  (attack lunge / flee pitch / topple) — see gait.ts `root:false`. */
function driveGait(L: Live, dt: number): void {
  if (!L.gaitArch) return;
  const pos = L.group.position;
  const speed = dt > 0 ? Math.hypot(pos.x - L.lastX, pos.z - L.lastZ) / dt : 0;
  L.lastX = pos.x; L.lastZ = pos.z;
  const ud = L.group.userData;
  ud.studioMoving = speed > GAIT_WALK_SPEED;
  ud.studioRunning = speed > GAIT_RUN_SPEED;
  // A humanoid NPC gets the SAME per-weapon pose pass as the player. Holding a
  // weapon: its `carry` stance while idle, `ready` while engaged, and the weapon's
  // own attack clip on a swing (e.g. an archer drawing its bow). Bare-handed: the
  // shared unarmed jab — a goblin punch. Either way we convert the gait's generic
  // one-shot attack trigger (`studioAttack`, fired off net.ts's atkSeq edge) into a
  // "primary" swing so the RESOLVED clip plays instead of the bare arm throw —
  // "primary" resolves to the weapon's attack, falling back to the jab when unarmed
  // (pose.ts selClip). Non-humanoid / bespoke ("parts") / coded NPCs have no rig and
  // keep the plain limb gait untouched.
  const rig = L.rig;
  if (rig) {
    ud.combatReady = L.engaged; // armed ready stances + unarmed boxer guard
    if (ud.studioAttack) { startRigAttack(L.group, "primary"); ud.studioAttack = false; }
  }
  animateGait(L.gaitArch, L.group, dt, 0, { root: false });
  // After the limb gait so the stance/swing wins over it (same ordering rule the
  // player uses — updatePlayer runs the gait then applyWeaponPose). Unarmed + not
  // mid-swing, applyWeaponPose is a no-op, so the idle/walk gait pose stands.
  // Pass gait swing so carry overlays match the player (one-hand / bow free-arm
  // + 2H (polearm/greatsword) coupled upper-body rock — pose.ts).
  if (rig) {
    const gs = gaitSnapshot(L.group);
    const swing = Math.sin(gs.phase);
    const armAmp = (NPC_PROFILE.armAmp + gs.runBlend * NPC_PROFILE.armAmpRun) * gs.walkBlend;
    applyWeaponPose(L.group, rig, dt, { swing, armAmp });
    // Body pose (seated/prone/…): the blended full-body layer, LAST so it wins
    // over the stance. Root integration is the one exception to "root belongs
    // to net.ts/combat.ts": while a pose is live we own y (absolute over
    // L.feetY — net re-syncs feetY every snapshot) + pitch/roll; topple never
    // co-runs (defeated NPCs bail before driveGait) and the flee reset above
    // runs before us, so both stay winners in their own states.
    if (applyBodyPose(L.group, rig, dt, _npcPoseOut)) {
      pos.y = L.feetY + _npcPoseOut.dy;
      L.group.rotation.x = _npcPoseOut.pitch;
      L.group.rotation.z = _npcPoseOut.roll;
    }
  }
}
const _npcPoseOut = { dy: 0, pitch: 0, roll: 0 };

/** Build every NPC into the scene, append their colliders, and prep their bubbles. */
export function initNpcs(scene: THREE.Scene, colliders: Collider[]): void {
  const container = document.getElementById("npcs")!;
  // Only the ACTIVE map's roster (plan 21) — building every map's rigs/DOM on
  // one client is scaling-cliff 1; other maps' NPCs simply don't exist here.
  for (const spec of NPCS_BY_MAP.get(ACTIVE_MAP_ID) ?? NPCS) {
    const { group, collider } = buildNpc(spec);
    dressNpc(group, spec); // held/offhand slot items + seeded gait jitter

    const height = spec.height ?? 3;
    let label: THREE.Sprite | null = null;
    if (spec.nameplate !== false) {
      label = makeNameLabel(spec.name);
      label.position.set(0, height + 0.3, 0); // float just above the head (spec.height = head-top) so it doesn't cover the face
      group.add(label);
    }

    // Snap the feet onto whatever floor/terrain is actually under the spawn spot,
    // using spec.y only as the starting guess (so a structure-floor NPC resolves
    // to its floor and a ground NPC resolves to the terrain grade). This means
    // authors no longer have to hand-compute terrain height for stationary NPCs —
    // spec.y just needs to be in the right ballpark to pick the right floor.
    const feetY = surfaceAt(spec.x, spec.z, spec.y, navWorld.surfaces);
    group.position.y = feetY;
    // Authored idle pose (map spawn `pose`) — statics hold it from here on;
    // dynamics get it re-confirmed (or cleared) by every snapshot.
    group.userData.bodyPose = spec.pose ?? null;
    if (collider) {
      // Re-base the collider's vertical extent on the resolved feet height.
      const h = collider.maxY - collider.minY;
      collider.minY = feetY;
      collider.maxY = feetY + h;
    }

    scene.add(group);
    if (collider) colliders.push(collider);

    const bubble = document.createElement("div");
    bubble.className = "bubble";
    bubble.style.display = "none";
    container.appendChild(bubble);

    // Fightable NPCs get an overhead HP + stamina bar (shown only while engaged),
    // projected over the head each frame just like the speech bubble.
    let cbarWrap: HTMLDivElement | null = null;
    let hpFill: HTMLDivElement | null = null;
    let staFill: HTMLDivElement | null = null;
    let hpLabel: HTMLSpanElement | null = null;
    let staLabel: HTMLSpanElement | null = null;
    let statusIcons: HTMLDivElement | null = null;
    if (spec.combat) {
      cbarWrap = document.createElement("div");
      cbarWrap.className = "cbar-wrap";
      cbarWrap.style.display = "none";
      const hp = makeCbar("hp");
      const sta = makeCbar("sta");
      hpFill = hp.fill; hpLabel = hp.label;
      staFill = sta.fill; staLabel = sta.label;
      statusIcons = makeStatusIcons();
      cbarWrap.append(hp.bar, sta.bar, statusIcons);
      container.appendChild(cbarWrap);
    }

    const record: Live = {
      spec,
      group,
      collider: collider && !("kind" in collider) ? (collider as CircleCollider) : null,
      bubble,
      label,
      height,
      feetY,
      line: -1,
      inRange: false,
      bubbleActive: false,
      bubbleCd: 0,
      fightable: !!spec.combat,
      hp: spec.combat?.hp ?? 0,
      maxHp: spec.combat?.hp ?? 0,
      stamina: spec.combat?.stamina ?? 0,
      maxStamina: spec.combat?.stamina ?? 0,
      engaged: false,
      defeated: false,
      defeatT: 0,
      cbarWrap,
      hpFill,
      staFill,
      hpLabel,
      staLabel,
      statusIcons,
      snare: false,
      stun: false,
      slow: false,
      talkable: !!spec.talk,
      talker: null,
      air: false,
      talkingToMe: false,
      tgt: null,
      fleeing: false,
      scamT: 0,
      gaitArch: gaitArchOf(spec),
      lastX: spec.x, lastZ: spec.z,
      // Humanoid NPCs that may hold a weapon get a cached rig for the weapon-pose
      // pass; dressNpc has already run equipRig, so userData.weaponSpec is set.
      rig: gaitArchOf(spec) === "humanoid" ? collectRig(group) : null,
      netHidden: false,
    };
    live.push(record);
    byId.set(spec.id, record);
    // Dynamic NPCs are interest-managed: start hidden/non-solid and appear on
    // their first enter from the server. Static NPCs render immediately.
    if (isDynamic(spec)) npcNetHide(record);
  }
}

/** Tear down EVERY live NPC — the mirror of initNpcs, for the in-session map
 *  swap (plan 24; game.ts swapWorld). Scene groups + their geometries go
 *  (meshkit disposeGroup skips the shared unit boxes; body materials stay —
 *  they draw from the process-wide toonMat cache), label sprites release their
 *  baked canvas textures (the net.ts remote-label pattern), and the projected
 *  DOM (bubble + combat bars) unhooks. Colliders need nothing: they live in
 *  the old world's colliders array, which dies with it. */
export function disposeNpcs(scene: THREE.Scene): void {
  for (const L of live) {
    scene.remove(L.group);
    // Hide too: a stale reference (e.g. a focus set the same frame) self-heals
    // via the visibility guards instead of pointing at a disposed rig forever.
    L.group.visible = false;
    disposeGroup(L.group);
    if (L.label) {
      L.label.material.map?.dispose();
      L.label.material.dispose();
    }
    L.bubble.remove();
    L.cbarWrap?.remove();
  }
  live.length = 0;
  byId.clear();
}

/** Take an NPC out of our world mirror (it left our area of interest): invisible,
 *  non-solid, no lingering bubble/bars. net.ts calls this on `leave.npcs`; the
 *  focus system auto-releases it via the group.visible guard next frame. */
export function npcNetHide(L: Live): void {
  L.netHidden = true;
  L.group.visible = false;
  L.bubble.style.display = "none";
  if (L.cbarWrap) L.cbarWrap.style.display = "none";
  if (L.collider) { L.collider.x = 1e6; L.collider.z = 1e6; L.collider.r = 0; }
  L.inRange = false;
  L.bubbleActive = false;
  // Clear the server-mirrored live flags: no fresh snapshots will update them
  // while hidden, and a stale engaged/fleeing would keep combat.ts projecting
  // bars (or the scamper pose) at the NPC's last position.
  L.engaged = false;
  L.talker = null;
  L.talkingToMe = false;
  L.tgt = null; // a hidden NPC can't be auto-engaging us
  L.fleeing = false;
}

/** Bring an NPC back into our world mirror (it entered our area of interest).
 *  Alive: stand it up + restore its collider (net.ts repositions from the wire).
 *  Defeated: snap past the corpse fade and HIDE — the client doesn't know how
 *  long the server death has remaining, so late joiners never see multi-minute
 *  corpses. Collider stays off (walk-through) either way. */
export function npcNetShow(L: Live, defeated: boolean): void {
  L.netHidden = false;
  L.defeated = defeated;
  if (defeated) {
    // Past CORPSE_LINGER + CORPSE_FADE (combat.ts) so advanceDefeat stays hidden.
    L.defeatT = 999;
    L.group.rotation.z = Math.PI / 2;
    L.group.scale.setScalar(1);
    L.group.visible = false;
    if (L.collider) { L.collider.x = 1e6; L.collider.z = 1e6; L.collider.r = 0; }
  } else {
    L.defeatT = 0;
    L.group.rotation.z = 0;
    L.group.scale.setScalar(1);
    L.group.visible = true;
    if (L.collider) L.collider.r = L.spec.colliderR ?? 0.6;
  }
}

/**
 * Per-frame: position each NPC's speech bubble (NPC movement itself is owned by
 * the server and applied in net.ts `updateNetNpcs`, which runs just before this).
 * @param active whether proximity should be tested at all (false on the sign-in screen).
 */
export function updateNpcs(camera: THREE.Camera, player: THREE.Object3D, active: boolean, dt: number): void {
  const W = window.innerWidth, H = window.innerHeight;
  for (const L of live) {
    // Chat-bubble cooldown ticks down every frame, in range or not, so the quiet
    // window is wall-clock (pacing away and back doesn't dodge it).
    if (L.bubbleCd > 0) L.bubbleCd = Math.max(0, L.bubbleCd - dt);

    // Out of our interest area: fully dormant (the cutaway pass below would
    // otherwise flip group.visible back on every frame).
    if (L.netHidden) {
      L.group.visible = false;
      L.bubble.style.display = "none";
      if (L.cbarWrap) L.cbarWrap.style.display = "none";
      L.inRange = false;
      L.bubbleActive = false;
      continue;
    }
    // Defeated NPCs are hidden + inert; combat.ts drives the collapse, we just
    // make sure nothing (bubble/bars) lingers over them.
    if (L.defeated) {
      L.bubble.style.display = "none";
      if (L.cbarWrap) L.cbarWrap.style.display = "none";
      if (L.label) L.label.visible = false; // no name tag over a corpse
      if (L.group.rotation.x !== 0) L.group.rotation.x = 0; // clear any leftover scamper tilt (topple owns rotation.z)
      L.inRange = false;
      L.bubbleActive = false;
      continue;
    }

    const pos = L.group.position;

    // Hide the NPC (and its bubble) if it stands on a level that's currently cut
    // away — otherwise it'd float once the floor under it fades out. Also hide
    // outdoor NPCs while the local player is indoors (exterior blackout).
    const occluded =
      occludedByCutaway(pos.x, pos.z, L.feetY) ||
      hideOutdoorWhileIndoor(pos.x, pos.z);
    L.group.visible = !occluded;
    if (occluded) { L.bubble.style.display = "none"; L.inRange = false; L.bubbleActive = false; continue; }

    // Animation distance gate: fog is fully opaque by ~135u and the camera far
    // plane clips at CAM_FAR (170), but interest management keeps NPCs live out
    // to ~375u — one past ANIM_RADIUS of the CAMERA is provably off-screen, so
    // the whole gait/pose pass (the priciest per-NPC CPU) is skipped and the
    // networked transform carries it. Resumes seamlessly on re-approach.
    const camP = camera.position;
    const animate =
      (pos.x - camP.x) ** 2 + (pos.z - camP.z) ** 2 < ANIM_RADIUS * ANIM_RADIUS;
    if (!animate) {
      // Frozen: skip the gait AND the whole bubble/label tail below — every
      // range in it (bubble radius 4-6, NAMEPLATE_RANGE 16) is far inside
      // ANIM_RADIUS, so the tail could only ever conclude "hidden" anyway.
      // Guarded writes pin that hidden state (the netHidden-branch idiom) so
      // nothing lingers if the NPC crossed the boundary mid-bubble.
      if (L.group.rotation.x !== 0) L.group.rotation.x = 0; // no stale scamper pitch
      if (L.label) L.label.visible = false;
      if (L.bubble.style.display !== "none") L.bubble.style.display = "none";
      L.inRange = false;
      L.bubbleActive = false;
      continue;
    }

    // Scamper pose: while fleeing (server-set), add a fast bounding hop + a slight
    // forward pitch on top of the networked transform so a bolt reads as frantic,
    // not a fast glide. Reset the pitch when calm. (Position.y is re-set by net.ts
    // every frame, so this offset never accumulates.)
    if (L.fleeing) {
      L.scamT += dt * 17;
      pos.y += Math.abs(Math.sin(L.scamT)) * 0.18;
      L.group.rotation.x = 0.14;
    } else if (L.group.rotation.x !== 0) {
      L.group.rotation.x = 0;
    }

    // Shared archetype gait: swing the legs/wings for walk/run (inferred from the
    // networked speed) + play a limb attack on the atkSeq edge. Limb-only, so it
    // layers under the flee pitch / attack lunge / topple that own the root above.
    driveGait(L, dt);

    const r = L.spec.radius ?? 6;
    const d = active ? Math.hypot(player.position.x - pos.x, player.position.z - pos.z) : Infinity;
    const inRange = d <= r;

    // Name tag: only within reach of the player, so distant NPCs stay clean —
    // and never over an NPC we're mid-conversation with (the cutscene frames it
    // close-up; the tag would sit across its face). While engaged the HP/STA
    // bars own the head space, so the plate drops too. The sprite is a child of
    // the group, so the netHidden/occluded branches above already hide it via
    // group.visible; this handles near-vs-far + talk + combat.
    if (L.label) L.label.visible = !L.talkingToMe && !L.engaged && d <= NAMEPLATE_RANGE;

    // On entering range, greet with the next canned line — but only if off cooldown,
    // so a re-approach within BUBBLE_COOLDOWN stays silent. bubbleActive gates the
    // display below so a cooldown-blocked entry shows nothing (not a stale line).
    if (inRange && !L.inRange) {
      if (L.bubbleCd <= 0) {
        L.line = (L.line + 1) % L.spec.dialogue.length;
        L.bubble.textContent = L.spec.dialogue[L.line];
        L.bubbleActive = true;
        L.bubbleCd = BUBBLE_COOLDOWN;
      }
    }
    L.inRange = inRange;

    if (!inRange) {
      L.bubble.style.display = "none";
      L.bubbleActive = false; // left range → next entry is a fresh (cooldown-gated) greeting
      continue;
    }

    // Suppress the proximity chat-bubble while engaged, mid-conversation, or when this
    // approach didn't greet (on cooldown): in combat the player is right on top of the
    // NPC, during a talk the dialogue HUD owns the exchange, and a cooldown re-entry
    // should stay quiet. It re-appears on the next off-cooldown approach.
    if (L.engaged || L.talkingToMe || !L.bubbleActive) {
      L.bubble.style.display = "none";
      continue;
    }

    // Project the head anchor (tracks the NPC) to screen pixels; skip if behind us.
    _v.set(pos.x, L.feetY + L.height, pos.z).project(camera);
    if (_v.z > 1) {
      L.bubble.style.display = "none";
      continue;
    }
    L.bubble.style.display = "block";
    L.bubble.style.left = `${(_v.x * 0.5 + 0.5) * W}px`;
    L.bubble.style.top = `${(-_v.y * 0.5 + 0.5) * H}px`;
  }
}
